import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, onSnapshot, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { Layout } from '../components/layout/Layout';
import '../styles/curriculum-manager.css';

const DIFF_CONFIG = {
  Beginner:     { color: '#10B981', emoji: '🌱' },
  Intermediate: { color: '#F59E0B', emoji: '📈' },
  Advanced:     { color: '#EF4444', emoji: '⭐' },
};

function StatusChip({ status }) {
  const MAP = {
    draft:     { bg: '#F3F4F6', border: '#D1D5DB', color: '#6B7280', label: 'Draft' },
    published: { bg: '#D1FAE5', border: '#6EE7B7', color: '#065F46', label: 'Published' },
    archived:  { bg: '#F3F4F6', border: '#D1D5DB', color: '#9CA3AF', label: 'Archived' },
  };
  const s = MAP[status] || MAP.draft;
  return (
    <span style={{
      padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700,
      background: s.bg, border: `1.5px solid ${s.border}`, color: s.color,
    }}>
      {s.label}
    </span>
  );
}

function genStepId() {
  return 's_' + Math.random().toString(36).slice(2, 10);
}

function compressImage(file, maxPx = 800, quality = 0.82) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxPx / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width  = Math.round(img.width  * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(resolve, 'image/jpeg', quality);
    };
    img.src = URL.createObjectURL(file);
  });
}

// ── A single editable step row ────────────────────────────────────────────────

function StepRow({ step, index, total, deckId, onChange, onMoveUp, onMoveDown, onDelete }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef();

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const compressed = await compressImage(file);
      const path = `sequencing-decks/${deckId}/${step.id}-${Date.now()}.jpg`;
      const sRef = storageRef(storage, path);
      await uploadBytes(sRef, compressed);
      const url = await getDownloadURL(sRef);
      onChange({ ...step, imageUrl: url });
    } catch (err) {
      setError(err.message || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{
      background: '#fff', borderRadius: 16, border: '1.5px solid #E5E7EB',
      overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      display: 'flex', flexWrap: 'wrap',
    }}>
      {/* ── Image ──────────────────────────────────────────────────────── */}
      <div
        onClick={() => fileRef.current?.click()}
        style={{
          width: 160, minWidth: 140, flexShrink: 0, minHeight: 140,
          background: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', position: 'relative', overflow: 'hidden',
        }}
      >
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={e => handleFile(e.target.files[0])}
        />
        {uploading ? (
          <span className="gen-spinner-sm" />
        ) : step.imageUrl ? (
          <img src={step.imageUrl} alt={step.label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <div style={{ textAlign: 'center', color: '#9CA3AF', fontSize: 11, padding: 8 }}>
            <div style={{ fontSize: 24, marginBottom: 4 }}>📷</div>
            Click to upload
          </div>
        )}
        {step.imageUrl && !uploading && (
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: 10, fontWeight: 600,
            textAlign: 'center', padding: '4px 0',
          }}>
            Click to replace
          </div>
        )}
      </div>

      {/* ── Fields + actions ─────────────────────────────────────────── */}
      <div style={{ flex: 1, padding: '14px 16px', minWidth: 200, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 26, height: 26, borderRadius: '50%', background: '#8B2635', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12, fontWeight: 800, flexShrink: 0,
          }}>
            {index + 1}
          </div>
          <input
            value={step.label}
            onChange={e => onChange({ ...step, label: e.target.value })}
            placeholder="Step label, e.g. Get toothbrush"
            style={{
              flex: 1, fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
              padding: '8px 12px', border: '1.5px solid #E5E7EB', borderRadius: 8,
              outline: 'none', color: '#111827', background: '#F9FAFB',
            }}
          />
        </div>

        {error && <p style={{ fontSize: 12, color: '#DC2626', margin: 0 }}>{error}</p>}

        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button className="cm-btn" style={{ fontSize: 12 }} onClick={onMoveUp} disabled={index === 0}>
            ↑ Move Up
          </button>
          <button className="cm-btn" style={{ fontSize: 12 }} onClick={onMoveDown} disabled={index === total - 1}>
            ↓ Move Down
          </button>
          <button className="cm-btn cm-btn--danger" style={{ fontSize: 12 }} onClick={onDelete}>
            🗑 Delete Step
          </button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Activity Editor — manual content workflow. No AI, no Cloud Functions.
// Admin edits basic info + steps here, then saves draft / publishes directly
// to Firestore (gated by the isAdmin() security rule).
// ══════════════════════════════════════════════════════════════════════════════

export const ActivityReviewPage = () => {
  const { deckId } = useParams();
  const navigate    = useNavigate();

  const [deck,    setDeck]    = useState(null);
  const [loading, setLoading] = useState(true);

  // ── Editable local state ──────────────────────────────────────────────────
  const [title,        setTitle]        = useState('');
  const [description,  setDescription]  = useState('');
  const [category,     setCategory]     = useState('');
  const [subcategory,  setSubcategory]  = useState('');
  const [difficulty,   setDifficulty]   = useState('Beginner');
  const [tagsInput,    setTagsInput]    = useState('');
  const [steps,        setSteps]        = useState([]);

  const [saving,  setSaving]  = useState(false);
  const [saveErr, setSaveErr] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  const loadedOnce = useRef(false);

  // ── Subscribe to the deck; populate local state only on first load ──────
  useEffect(() => {
    if (!deckId) return;
    const unsub = onSnapshot(
      doc(db, 'generatedSequencingDecks', deckId),
      snap => {
        if (snap.exists()) {
          const d = { id: snap.id, ...snap.data() };
          setDeck(d);
          if (!loadedOnce.current) {
            setTitle(d.title || '');
            setDescription(d.description || '');
            setCategory(d.category || '');
            setSubcategory(d.subcategory || '');
            setDifficulty(d.difficulty || 'Beginner');
            setTagsInput((d.tags || []).join(', '));
            setSteps((d.steps || []).slice().sort((a, b) => a.order - b.order));
            loadedOnce.current = true;
          }
        }
        setLoading(false);
      },
      err => { console.error(err); setLoading(false); }
    );
    return unsub;
  }, [deckId]);

  // ── Step list operations ──────────────────────────────────────────────────
  const renumber = (arr) => arr.map((s, i) => ({ ...s, order: i + 1 }));

  const handleAddStep = () => {
    setSteps(prev => renumber([...prev, { id: genStepId(), order: prev.length + 1, label: '', imageUrl: null }]));
  };

  const handleStepChange = (idx, updated) => {
    setSteps(prev => prev.map((s, i) => i === idx ? updated : s));
  };

  const handleMoveUp = (idx) => {
    if (idx === 0) return;
    setSteps(prev => {
      const next = [...prev];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      return renumber(next);
    });
  };

  const handleMoveDown = (idx) => {
    setSteps(prev => {
      if (idx === prev.length - 1) return prev;
      const next = [...prev];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      return renumber(next);
    });
  };

  const handleDeleteStep = (idx) => {
    setSteps(prev => renumber(prev.filter((_, i) => i !== idx)));
  };

  // ── Save / publish / archive ──────────────────────────────────────────────

  const buildPayload = useCallback(() => ({
    title:       title.trim(),
    description: description.trim(),
    category:    category.trim(),
    subcategory: subcategory.trim(),
    difficulty,
    tags:        tagsInput.split(',').map(t => t.trim()).filter(Boolean),
    steps:       steps.map(s => ({
      id:       s.id,
      order:    s.order,
      label:    s.label.trim(),
      imageUrl: s.imageUrl || null,
      images:   s.imageUrl ? [s.imageUrl] : [],
      bgColor:  '#EEF2FF',
      emoji:    '🧩',
    })),
    stepCount: steps.length,
    updatedAt: serverTimestamp(),
  }), [title, description, category, subcategory, difficulty, tagsInput, steps]);

  const save = async (extra, { requireValid = false } = {}) => {
    if (requireValid) {
      if (!title.trim()) return setSaveErr('Title is required to publish.');
      if (steps.length < 1) return setSaveErr('Add at least one step before publishing.');
      if (steps.some(s => !s.label.trim())) return setSaveErr('Every step needs a label before publishing.');
    }
    setSaving(true);
    setSaveErr(null);
    try {
      await updateDoc(doc(db, 'generatedSequencingDecks', deckId), { ...buildPayload(), ...extra });
      setSavedAt(Date.now());
    } catch (err) {
      setSaveErr(err.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft   = () => save({ status: 'draft' });
  const handleSaveChanges = () => save({});
  const handlePublish     = () => save({ status: 'published', publishedAt: serverTimestamp() }, { requireValid: true });
  const handleUnpublish   = () => save({ status: 'draft' });
  const handleArchive     = () => save({ status: 'archived' });
  const handleRestore     = () => save({ status: 'draft' });

  if (loading) {
    return (
      <Layout>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
          minHeight: '60vh', color: '#9CA3AF', fontSize: 14 }}>
          Loading…
        </div>
      </Layout>
    );
  }

  if (!deck) {
    return (
      <Layout>
        <div className="cm-page">
          <div style={{ textAlign: 'center', padding: '80px 24px', color: '#6B7280' }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>🔍</div>
            <h2 style={{ margin: '0 0 8px', color: '#111827' }}>Activity not found</h2>
            <button className="cm-btn cm-btn--primary" onClick={() => navigate('/admin/curriculum')}>
              Back to Activity Manager
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  const isDraft     = deck.status === 'draft';
  const isPublished = deck.status === 'published';
  const isArchived  = deck.status === 'archived';

  return (
    <Layout>
      <div className="cm-page">

        {/* ── Top navigation ──────────────────────────────────────────── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
          <button className="cm-btn" style={{ fontSize: 12 }} onClick={() => navigate('/admin/curriculum')}>
            ← Activity Manager
          </button>
          <a
            className="cm-btn"
            style={{ fontSize: 12, textDecoration: 'none' }}
            href={`/sequencing/generated/${deckId}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            ▶ Preview as Learner
          </a>
          <div style={{ flex: 1 }} />
          <StatusChip status={deck.status} />
        </div>

        {/* ── Basic info ─────────────────────────────────────────────── */}
        <div style={{
          background: '#fff', borderRadius: 20, border: '1.5px solid #E5E7EB',
          padding: '24px 28px', marginBottom: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        }}>
          <label className="cm-subtitle" style={{ display: 'block', fontSize: 11, fontWeight: 700,
            color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
            Title
          </label>
          <input
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="Activity title"
            style={{
              width: '100%', boxSizing: 'border-box', fontSize: 22, fontWeight: 800,
              fontFamily: 'inherit', padding: '6px 0', border: 'none', borderBottom: '2px solid #E5E7EB',
              outline: 'none', color: '#111827', marginBottom: 16, background: 'transparent',
            }}
          />

          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#9CA3AF',
            textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
            Description
          </label>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            rows={2}
            placeholder="Brief description (optional)"
            style={{
              width: '100%', boxSizing: 'border-box', resize: 'vertical', fontSize: 13,
              fontFamily: 'inherit', padding: '8px 12px', border: '1.5px solid #E5E7EB',
              borderRadius: 8, outline: 'none', color: '#374151', marginBottom: 16, lineHeight: 1.5,
            }}
          />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#9CA3AF',
                textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Category</label>
              <input
                value={category}
                onChange={e => setCategory(e.target.value)}
                style={{
                  width: '100%', boxSizing: 'border-box', fontSize: 13, fontFamily: 'inherit',
                  padding: '8px 12px', border: '1.5px solid #E5E7EB', borderRadius: 8,
                  outline: 'none', color: '#374151',
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#9CA3AF',
                textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Subcategory</label>
              <input
                value={subcategory}
                onChange={e => setSubcategory(e.target.value)}
                style={{
                  width: '100%', boxSizing: 'border-box', fontSize: 13, fontFamily: 'inherit',
                  padding: '8px 12px', border: '1.5px solid #E5E7EB', borderRadius: 8,
                  outline: 'none', color: '#374151',
                }}
              />
            </div>
          </div>

          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#9CA3AF',
            textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Difficulty</label>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            {Object.entries(DIFF_CONFIG).map(([level, cfg]) => (
              <button
                key={level}
                onClick={() => setDifficulty(level)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px',
                  borderRadius: 20, border: '1.5px solid',
                  borderColor: difficulty === level ? cfg.color : '#E5E7EB',
                  background: difficulty === level ? cfg.color + '18' : '#F9FAFB',
                  color: difficulty === level ? cfg.color : '#6B7280',
                  fontSize: 12, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer',
                }}
              >
                {cfg.emoji} {level}
              </button>
            ))}
          </div>

          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#9CA3AF',
            textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Tags</label>
          <input
            value={tagsInput}
            onChange={e => setTagsInput(e.target.value)}
            placeholder="comma, separated, tags"
            style={{
              width: '100%', boxSizing: 'border-box', fontSize: 13, fontFamily: 'inherit',
              padding: '8px 12px', border: '1.5px solid #E5E7EB', borderRadius: 8,
              outline: 'none', color: '#374151',
            }}
          />
        </div>

        {/* ── Status actions ─────────────────────────────────────────── */}
        <div style={{
          background: isPublished ? '#F0FDF4' : isArchived ? '#F9FAFB' : '#FFFBEB',
          border: `1.5px solid ${isPublished ? '#86EFAC' : isArchived ? '#E5E7EB' : '#FCD34D'}`,
          borderRadius: 14, padding: '16px 20px', marginBottom: 20,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
        }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>
              {isPublished ? '✓ Published — visible to customers' : isArchived ? 'Archived — hidden from everyone' : 'Draft — not visible to customers yet'}
            </div>
            {savedAt && <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>Saved ✓</div>}
            {saveErr && <div style={{ fontSize: 12, color: '#DC2626', marginTop: 2 }}>{saveErr}</div>}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {isDraft && (
              <>
                <button className="cm-btn" disabled={saving} onClick={handleSaveDraft}>
                  {saving ? 'Saving…' : '💾 Save Draft'}
                </button>
                <button className="cm-btn cm-btn--primary" disabled={saving} onClick={handlePublish}>
                  {saving ? 'Publishing…' : '▶ Publish'}
                </button>
              </>
            )}
            {isPublished && (
              <>
                <button className="cm-btn" disabled={saving} onClick={handleSaveChanges}>
                  {saving ? 'Saving…' : '💾 Save Changes'}
                </button>
                <button className="cm-btn" disabled={saving} onClick={handleUnpublish}>
                  Unpublish
                </button>
                <button className="cm-btn cm-btn--danger" disabled={saving} onClick={handleArchive}>
                  Archive
                </button>
              </>
            )}
            {isArchived && (
              <button className="cm-btn cm-btn--primary" disabled={saving} onClick={handleRestore}>
                Restore to Draft
              </button>
            )}
          </div>
        </div>

        {/* ── Steps ──────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h2 style={{ fontSize: 14, fontWeight: 800, color: '#374151', textTransform: 'uppercase',
            letterSpacing: '0.06em', margin: 0 }}>
            Steps ({steps.length})
          </h2>
          <button className="cm-btn cm-btn--primary" style={{ fontSize: 12 }} onClick={handleAddStep}>
            + Add Step
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
          {steps.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#9CA3AF', fontSize: 13 }}>
              No steps yet — click "+ Add Step" to begin.
            </div>
          )}
          {steps.map((step, idx) => (
            <StepRow
              key={step.id}
              step={step}
              index={idx}
              total={steps.length}
              deckId={deckId}
              onChange={updated => handleStepChange(idx, updated)}
              onMoveUp={() => handleMoveUp(idx)}
              onMoveDown={() => handleMoveDown(idx)}
              onDelete={() => handleDeleteStep(idx)}
            />
          ))}
        </div>

        {/* ── Bottom save bar ────────────────────────────────────────── */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          {saveErr && <span style={{ fontSize: 12, color: '#DC2626', alignSelf: 'center' }}>{saveErr}</span>}
          {isDraft && (
            <>
              <button className="cm-btn" disabled={saving} onClick={handleSaveDraft}>
                {saving ? 'Saving…' : '💾 Save Draft'}
              </button>
              <button className="cm-btn cm-btn--primary" disabled={saving} onClick={handlePublish}>
                {saving ? 'Publishing…' : '▶ Publish'}
              </button>
            </>
          )}
          {isPublished && (
            <button className="cm-btn cm-btn--primary" disabled={saving} onClick={handleSaveChanges}>
              {saving ? 'Saving…' : '💾 Save Changes'}
            </button>
          )}
          {isArchived && (
            <button className="cm-btn cm-btn--primary" disabled={saving} onClick={handleRestore}>
              Restore to Draft
            </button>
          )}
        </div>

      </div>
    </Layout>
  );
};
